<template>
  <div class="skills-list">
    <!-- Filters -->
    <div class="filters">
      <input
        v-model="search"
        @input="debouncedLoad"
        type="text"
        class="filter-input"
        :placeholder="$t('bo.esco.searchSkills')"
      />
      <select v-model="skillType" @change="loadSkills" class="filter-select">
        <option value="">{{ $t('bo.esco.allTypes') }}</option>
        <option value="skill">{{ $t('bo.esco.typeSkill') }}</option>
        <option value="knowledge">{{ $t('bo.esco.typeKnowledge') }}</option>
        <option value="attitude">{{ $t('bo.esco.typeAttitude') }}</option>
      </select>
      <select v-model="withOverride" @change="loadSkills" class="filter-select">
        <option value="">{{ $t('bo.esco.allOverrides') }}</option>
        <option value="true">{{ $t('bo.esco.manuallyEdited') }}</option>
        <option value="false">{{ $t('bo.esco.notEdited') }}</option>
      </select>
    </div>

    <!-- Bulk actions bar -->
    <div v-if="selectedIds.size > 0" class="bulk-bar">
      <span class="bulk-count">{{ $t('bo.esco.selectedCount', { count: selectedIds.size }) }}</span>
      <button @click="selectedIds.clear()" class="btn-bulk-cancel">{{ $t('bo.common.deselectAll') }}</button>
      <button class="btn-bulk btn-bulk-archive" disabled :title="$t('bo.esco.archiveRequiresBackend')">
        {{ $t('bo.common.archive') }}
      </button>
      <button
        v-if="selectedIds.size >= 2"
        @click="openMerge"
        class="btn-bulk btn-bulk-merge"
      >
        {{ $t('bo.esco.mergeCount', { count: selectedIds.size }) }}
      </button>
    </div>

    <div v-if="loading" class="loading">{{ $t('bo.esco.loadingSkills') }}</div>
    <div v-else-if="error" class="error-msg">{{ error }}</div>
    <div v-else-if="skills.length === 0" class="empty">{{ $t('bo.esco.noSkillsFound') }}</div>

    <table v-else class="table">
      <thead>
        <tr>
          <th class="col-check">
            <input
              type="checkbox"
              :checked="allSelected"
              :indeterminate="someSelected"
              @change="toggleSelectAll"
            />
          </th>
          <th>{{ $t('bo.common.name') }}</th>
          <th>{{ $t('bo.esco.colEscoId') }}</th>
          <th>{{ $t('bo.esco.colType') }}</th>
          <th>{{ $t('bo.esco.colParentGroup') }}</th>
          <th>{{ $t('bo.esco.colOverride') }}</th>
          <th>{{ $t('bo.common.actions') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="skill in skills" :key="skill.id" :class="{ selected: selectedIds.has(skill.id) }">
          <td class="col-check">
            <input
              type="checkbox"
              :checked="selectedIds.has(skill.id)"
              @change="toggleSelect(skill.id)"
            />
          </td>
          <td class="col-name">{{ skill.title }}</td>
          <td class="col-esco">
            <span class="esco-id">{{ skill.esco_uri ? skill.esco_uri.split('/').pop() : '—' }}</span>
          </td>
          <td>
            <span :class="['badge-type', `badge-type-${skill.esco_skill_type}`]">
              {{ skill.esco_skill_type || '—' }}
            </span>
          </td>
          <td>
            <span v-if="skill.parent_title" class="parent-label">{{ skill.parent_title }}</span>
            <span v-else-if="skill.skill_group" class="parent-label parent-label--group">{{ skill.skill_group }}</span>
            <span v-else class="text-muted">—</span>
          </td>
          <td>
            <span v-if="skill.manually_edited" class="badge-override">{{ $t('bo.esco.badgeEdited') }}</span>
          </td>
          <td>
            <button @click="openDetail(skill.id)" class="btn-edit">{{ $t('bo.common.edit') }}</button>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Pagination -->
    <div v-if="pagination.last_page > 1" class="pagination">
      <button :disabled="pagination.current_page === 1" @click="loadPage(pagination.current_page - 1)" class="page-btn">{{ $t('bo.common.previous') }}</button>
      <span class="page-info">{{ $t('bo.esco.pageInfo', { current: pagination.current_page, last: pagination.last_page, total: pagination.total }) }}</span>
      <button :disabled="pagination.current_page === pagination.last_page" @click="loadPage(pagination.current_page + 1)" class="page-btn">{{ $t('bo.common.next') }}</button>
    </div>

    <!-- Detail modal -->
    <SkillDetail
      v-if="detailId"
      :skill-id="detailId"
      @close="detailId = null"
      @updated="handleUpdated"
    />

    <!-- Merge modal -->
    <MergeSkillsModal
      v-if="showMerge"
      :skills="selectedSkillObjects"
      @close="showMerge = false"
      @merged="handleMerged"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import escoService from '@/services/escoService'

const { t } = useI18n()
import SkillDetail from './SkillDetail.vue'
import MergeSkillsModal from './MergeSkillsModal.vue'

const skills = ref([])
const loading = ref(false)
const error = ref('')
const search = ref('')
const skillType = ref('')
const withOverride = ref('')
const pagination = ref({ current_page: 1, last_page: 1, total: 0 })
const detailId = ref(null)
const selectedIds = ref(new Set())
const showMerge = ref(false)

const selectedSkillObjects = computed(() =>
  skills.value.filter(s => selectedIds.value.has(s.id))
)

let debounceTimer = null

const allSelected = computed(() =>
  skills.value.length > 0 && skills.value.every(s => selectedIds.value.has(s.id))
)
const someSelected = computed(() =>
  selectedIds.value.size > 0 && !allSelected.value
)

const toggleSelectAll = () => {
  if (allSelected.value) {
    skills.value.forEach(s => selectedIds.value.delete(s.id))
  } else {
    skills.value.forEach(s => selectedIds.value.add(s.id))
  }
  selectedIds.value = new Set(selectedIds.value)
}

const toggleSelect = (id) => {
  const next = new Set(selectedIds.value)
  if (next.has(id)) next.delete(id)
  else next.add(id)
  selectedIds.value = next
}

const debouncedLoad = () => {
  clearTimeout(debounceTimer)
  debounceTimer = setTimeout(loadSkills, 400)
}

const loadSkills = async (page = 1) => {
  loading.value = true
  error.value = ''
  selectedIds.value = new Set()
  try {
    const params = { page, per_page: 50 }
    if (search.value) params.search = search.value
    if (skillType.value) params.skill_type = skillType.value
    if (withOverride.value !== '') params.with_override = withOverride.value
    const res = await escoService.getSkills(params)
    skills.value = res.data?.data || []
    pagination.value = {
      current_page: res.data?.current_page || 1,
      last_page: res.data?.last_page || 1,
      total: res.data?.total || 0,
    }
  } catch {
    error.value = t('bo.esco.failedLoadSkills')
  } finally {
    loading.value = false
  }
}

const loadPage = (page) => loadSkills(page)
const openDetail = (id) => { detailId.value = id }
const handleUpdated = () => loadSkills(pagination.value.current_page)
const openMerge = () => { showMerge.value = true }
const handleMerged = () => {
  showMerge.value = false
  selectedIds.value = new Set()
  loadSkills(pagination.value.current_page)
}

onMounted(loadSkills)
</script>

<style scoped>
.skills-list {
  background: white;
  border-radius: 0.75rem;
  border: 1px solid #e5e7eb;
  overflow: hidden;
}

.filters {
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.filter-input {
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  flex: 1;
  min-width: 200px;
}

.filter-input:focus { outline: none; border-color: #3b82f6; }

.filter-select {
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  background: white;
}

.bulk-bar {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 1.25rem;
  background: #eff6ff;
  border-bottom: 1px solid #bfdbfe;
}

.bulk-count {
  font-size: 0.875rem;
  font-weight: 600;
  color: #1d4ed8;
}

.btn-bulk-cancel {
  padding: 0.3rem 0.65rem;
  border: 1px solid #93c5fd;
  background: white;
  color: #1d4ed8;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.8rem;
}

.btn-bulk {
  padding: 0.3rem 0.75rem;
  border-radius: 0.375rem;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid;
}

.btn-bulk-archive {
  border-color: #f59e0b;
  color: #b45309;
  background: white;
}

.btn-bulk-merge {
  border-color: #8b5cf6;
  color: #6d28d9;
  background: white;
}

.btn-bulk:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.loading, .empty {
  padding: 3rem;
  text-align: center;
  color: #6b7280;
}

.error-msg {
  padding: 1rem 1.25rem;
  color: #dc2626;
}

.table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.table th {
  padding: 0.75rem 1rem;
  text-align: left;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.table td {
  padding: 0.875rem 1rem;
  border-bottom: 1px solid #f3f4f6;
  vertical-align: top;
}

.table tr:last-child td { border-bottom: none; }
.table tr.selected td { background: #eff6ff; }

.col-check { width: 2.5rem; padding: 0.5rem 0.75rem !important; }
.col-name { font-weight: 500; color: #1f2937; max-width: 300px; }
.col-esco { font-family: monospace; }
.esco-id { font-size: 0.8rem; color: #6b7280; }

.badge-type {
  display: inline-block;
  padding: 0.15rem 0.45rem;
  border-radius: 0.25rem;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: capitalize;
}
.badge-type-skill { background: #dbeafe; color: #1e40af; }
.badge-type-knowledge { background: #d1fae5; color: #065f46; }
.badge-type-attitude { background: #fce7f3; color: #9d174d; }

.badge-override {
  display: inline-block;
  padding: 0.15rem 0.5rem;
  background: #fef3c7;
  color: #92400e;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
}

.btn-edit {
  padding: 0.3rem 0.65rem;
  border: 1px solid #3b82f6;
  color: #3b82f6;
  background: white;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 500;
}

.btn-edit:hover { background: #eff6ff; }

.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 1rem;
  border-top: 1px solid #e5e7eb;
}

.page-btn {
  padding: 0.4rem 0.9rem;
  border: 1px solid #d1d5db;
  background: white;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.875rem;
}

.page-btn:hover:not(:disabled) { background: #f9fafb; }
.page-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.page-info { font-size: 0.875rem; color: #6b7280; }

.parent-label {
  font-size: 0.8rem;
  color: #374151;
  background: #f3f4f6;
  padding: 0.15rem 0.5rem;
  border-radius: 0.25rem;
}
.parent-label--group { background: #ede9fe; color: #5b21b6; }
.text-muted { color: #9ca3af; }
</style>
